import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from "@capacitor/core";
import axios from 'axios';
import Api from '../../Api/Api.json';
import {
    VictoryChart,
    VictoryBar,
    VictoryAxis,
    VictoryLabel,
} from 'victory';

// Conditionally import JStyle only on native platforms
let JStyle = null;
if (Capacitor.isNativePlatform()) {
  const JStyleModule = require("../../JStylePlugin");
  JStyle = JStyleModule.default;
}

// Icons for activity metrics
const StepsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
  </svg>
);

const CaloriesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v20M2 12h20" />
    <path d="M12 2a10 10 0 0 1 0 20" />
    <path d="M12 2a10 10 0 0 0 0 20" />
  </svg>
);

const DistanceIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polygon points="10,8 16,12 10,16 10,8"></polygon>
  </svg>
);

const ExerciseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
    <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
  </svg>
);

const Activity = () => {
  const navigate = useNavigate();
  const [isExerciseActive, setIsExerciseActive] = useState(false);
  const [exerciseData, setExerciseData] = useState({
    stepCount: 0,
    calories: 0,
    distance: 0,
    heartRate: 0,
    activeTime: 0,
    temp: 0,
    bloodOxygen: 0
  });

  const [elapsedTime, setElapsedTime] = useState(0);
  const [status, setStatus] = useState('');
  const [showReminder, setShowReminder] = useState(false);
  const intervalRef = useRef(null);
  const timerRef = useRef(null);
  const reminderTimerRef = useRef(null);

  // Exercise Record state
  const [patientId, setPatientId] = useState(null);
  const [exerciseList, setExerciseList] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [exerciseError, setExerciseError] = useState(null);

  // Get patient ID from localStorage
  useEffect(() => {
    if (localStorage.getItem('user_info') !== null) {
      const userInfo = JSON.parse(localStorage.getItem('user_info'));
      setPatientId(userInfo.di_id);
    }
  }, []);

  // Add listener for real-time step data
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Web platform - device features not available");
      return;
    }

    // Add listener for real-time step data
    JStyle.addListener("rtStepData", handleStepData);

    return () => {
      if (JStyle) {
        JStyle.removeListener("rtStepData");
      }
      // Clean up timers
      if (reminderTimerRef.current) {
        clearTimeout(reminderTimerRef.current);
      }
    };
  }, []);

  const handleStepData = (stepResult) => {
    console.log("Real-time step data received:", stepResult);
    
    // Update exercise data with real device data
    setExerciseData({
      stepCount: parseInt(stepResult.step) || 0,
      calories: parseInt(stepResult.cal) || 0,
      distance: parseFloat(stepResult.distance) || 0,
      heartRate: parseInt(stepResult.heart) || 0,
      activeTime: parseInt(stepResult.active_time) || 0,
      temp: parseFloat(stepResult.temp) || 0,
      bloodOxygen: parseInt(stepResult.blood_oxygen) || 0
    });
  };







  const handleCardClick = (metricType) => {
    let dataType;
    
    switch(metricType) {
      case 'steps':
        dataType = 'dailysteps';
        break;
      case 'calories':
        dataType = 'dailycalories';
        break;
      case 'distance':
        dataType = 'dailydistance';
        break;
      default:
        return;
    }
    
    // Navigate to VisualizeWellnessData with data type
    navigate('/landing/tab3/VisualizeWellnessData', {
      state: {
        dataType: dataType
      }
    });
  };

  const startExercise = () => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setStatus("Device exercise tracking not available on web platform");
      return;
    }

    setIsExerciseActive(true);
    setElapsedTime(0);
    setStatus("Starting exercise tracking...");
    setShowReminder(false);

    // Reset exercise data
    setExerciseData({
      stepCount: 0,
      calories: 0,
      distance: 0,
      heartRate: 0,
      activeTime: 0,
      temp: 0,
      bloodOxygen: 0
    });

    // Call JStyle plugin to start real-time step tracking
    JStyle.getRealtimeStepData({ start: "true" })
      .then((result) => {
        console.log("Exercise tracking started:", result);
        setStatus("Exercise tracking active");
        
        // Start timer for elapsed time
        timerRef.current = setInterval(() => {
          setElapsedTime(prev => prev + 1);
        }, 1000);

        // Set 30-minute reminder
        reminderTimerRef.current = setTimeout(() => {
          setShowReminder(true);
          setStatus("30 minutes completed - check if you want to continue");
        }, 30 * 60 * 1000); // 30 minutes
      })
      .catch((error) => {
        console.error("Error starting exercise tracking:", error);
        setStatus("Failed to start exercise tracking");
        setIsExerciseActive(false);
      });
  };

  const stopExercise = () => {
    if (!Capacitor.isNativePlatform() || !JStyle) {
      setIsExerciseActive(false);
      return;
    }

    setStatus("Stopping exercise tracking...");
    
    // Call JStyle plugin to stop real-time step tracking
    JStyle.getRealtimeStepData({ start: "false" })
      .then((result) => {
        console.log("Exercise tracking stopped:", result);
        setStatus("Exercise tracking stopped");
        setIsExerciseActive(false);
        setShowReminder(false);
        
        // Clear timers
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        if (reminderTimerRef.current) {
          clearTimeout(reminderTimerRef.current);
          reminderTimerRef.current = null;
        }
      })
      .catch((error) => {
        console.error("Error stopping exercise tracking:", error);
        setStatus("Error stopping exercise tracking");
        setIsExerciseActive(false);
        setShowReminder(false);
        
        // Clear timers even if there's an error
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        if (reminderTimerRef.current) {
          clearTimeout(reminderTimerRef.current);
          reminderTimerRef.current = null;
        }
      });
  };

  // Cleanup intervals on component unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (reminderTimerRef.current) {
        clearTimeout(reminderTimerRef.current);
      }
    };
  }, []);

  const handleContinueExercise = () => {
    setShowReminder(false);
    setStatus("Exercise tracking continued");
    
    // Set another 30-minute reminder
    reminderTimerRef.current = setTimeout(() => {
      setShowReminder(true);
      setStatus("30 minutes completed - check if you want to continue");
    }, 30 * 60 * 1000); // 30 minutes
  };

  // Auto-stop if user doesn't respond to reminder within 1 minute
  useEffect(() => {
    if (showReminder) {
      const autoStopTimer = setTimeout(() => {
        console.log("Auto-stopping exercise due to no response to reminder");
        stopExercise();
      }, 60 * 1000); // 1 minute timeout

      return () => clearTimeout(autoStopTimer);
    }
  }, [showReminder]);

  const handleStopExercise = () => {
    stopExercise();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch exercise session list
  useEffect(() => {
    const fetchExerciseList = async () => {
      if (!patientId) return;
      
      setIsLoadingList(true);
      setExerciseError(null);
      
      try {
        const headers = { headers: { "Content-Type": "application/json" } };
        const url = Api.EXERCISE_SESSION_DATA_API;
        const requestData = {
          task: "get_exercise_session_list",
          patient_id: patientId
        };
        
        const response = await axios.post(url, requestData, headers);
        
        // Handle different response structures
        let responseData = response.data;
        if (responseData?.body) responseData = responseData.body;
        if (responseData?.data) responseData = responseData.data;
        
        if (responseData && Array.isArray(responseData)) {
          // Map API response to expected format
          const mappedData = responseData.map(item => {
            // Parse timestamp - API might return it as string or timestamp
            let timestamp;
            if (item.timestamp) {
              timestamp = typeof item.timestamp === 'string' 
                ? new Date(item.timestamp) 
                : new Date(item.timestamp * 1000); // if it's Unix timestamp
            } else if (item.datetime) {
              timestamp = new Date(item.datetime);
            } else {
              timestamp = new Date();
            }
            
            return {
              datetime: item.datetime || timestamp.toISOString(),
              timestamp: timestamp,
              // Store original timestamp for API call
              originalTimestamp: item.timestamp || item.datetime
            };
          });
          
          // Sort by datetime descending (newest first)
          const sortedData = mappedData.sort((a, b) => b.timestamp - a.timestamp);
          setExerciseList(sortedData);
          
          // Set first item as selected by default
          if (sortedData.length > 0) {
            setSelectedExercise(sortedData[0]);
          }
        } else {
          setExerciseList([]);
        }
      } catch (err) {
        console.error('Error fetching exercise session list:', err);
        setExerciseError('Failed to load exercise sessions. Please try again.');
        setExerciseList([]);
      } finally {
        setIsLoadingList(false);
      }
    };
    
    fetchExerciseList();
  }, [patientId]);
  
  // Fetch exercise session details when selected
  useEffect(() => {
    const fetchExerciseDetails = async () => {
      if (!patientId || !selectedExercise || !selectedExercise.originalTimestamp) return;
      
      setIsLoadingDetails(true);
      setExerciseError(null);
      
      try {
        const headers = { headers: { "Content-Type": "application/json" } };
        const url = Api.EXERCISE_SESSION_DATA_API;
        
        // Use original timestamp string as-is without timezone transformation
        const timestampForApi = typeof selectedExercise.originalTimestamp === 'string' 
          ? selectedExercise.originalTimestamp
          : selectedExercise.originalTimestamp instanceof Date
            ? selectedExercise.originalTimestamp.toISOString()
            : String(selectedExercise.originalTimestamp);
        
        const requestData = {
          task: "get_exercise_session_data",
          patient_id: patientId,
          timestamp: timestampForApi
        };
        
        const response = await axios.post(url, requestData, headers);
        
        // Handle different response structures
        let responseData = response.data;
        if (responseData?.body) responseData = responseData.body;
        if (responseData?.data) responseData = responseData.data;
        
        // Handle array response - take first item if array
        if (Array.isArray(responseData) && responseData.length > 0) {
          responseData = responseData[0];
        }
        
        if (responseData) {
          // Parse zone_summary and zone_map if they are JSON strings
          let parsedZoneSummary = null;
          let parsedZoneMap = null;
          
          if (responseData.zone_summary) {
            if (typeof responseData.zone_summary === 'string') {
              try {
                parsedZoneSummary = JSON.parse(responseData.zone_summary);
              } catch (e) {
                console.error('Error parsing zone_summary JSON:', e);
                parsedZoneSummary = null;
              }
            } else {
              parsedZoneSummary = responseData.zone_summary;
            }
          }
          
          if (responseData.zone_map) {
            if (typeof responseData.zone_map === 'string') {
              try {
                parsedZoneMap = JSON.parse(responseData.zone_map);
              } catch (e) {
                console.error('Error parsing zone_map JSON:', e);
                parsedZoneMap = null;
              }
            } else {
              parsedZoneMap = responseData.zone_map;
            }
          }
          
          // Merge API response with existing exercise data
          const updatedExercise = {
            ...selectedExercise,
            heartrate_min: responseData.heartrate_min ?? selectedExercise.heartrate_min,
            heartrate_max: responseData.heartrate_max ?? selectedExercise.heartrate_max,
            heartrate_avg: responseData.heartrate_avg ?? selectedExercise.heartrate_avg,
            total_time: responseData.total_time ?? selectedExercise.total_time,
            total_steps: responseData.total_steps ?? selectedExercise.total_steps,
            total_distance: responseData.total_distance ?? selectedExercise.total_distance,
            total_calories: responseData.total_calories ?? selectedExercise.total_calories,
            avg_speed: responseData.avg_speed ?? selectedExercise.avg_speed,
            vo2max: responseData.vo2max ?? selectedExercise.vo2max,
            cardio_recovery_score: responseData.cardio_recovery_score ?? responseData.cardio_recovery ?? selectedExercise.cardio_recovery_score,
            // Store parsed zone_summary and zone_map from API response
            zone_summary: parsedZoneSummary,
            zone_map: parsedZoneMap
          };
          
          setSelectedExercise(updatedExercise);
        }
      } catch (err) {
        console.error('Error fetching exercise session details:', err);
        setExerciseError('Failed to load exercise session details. Please try again.');
      } finally {
        setIsLoadingDetails(false);
      }
    };
    
    fetchExerciseDetails();
  }, [patientId, selectedExercise?.originalTimestamp]);

  const handleExerciseSelect = (exercise) => {
    setSelectedExercise(exercise);
    setIsDropdownOpen(false);
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  const formatDistance = (km) => {
    if (!km) return '0 km';
    return `${km.toFixed(2)} km`;
  };

  const formatSpeed = (kmh) => {
    if (!kmh) return '0 km/h';
    return `${kmh.toFixed(1)} km/h`;
  };

  // Process zone_map data for chart
  const zoneMapChartData = useMemo(() => {
    if (!selectedExercise || !selectedExercise.zone_map || !Array.isArray(selectedExercise.zone_map)) {
      return [];
    }

    const chartData = [];

    selectedExercise.zone_map.forEach((entry, index) => {
      // Handle zone = -1 (no data)
      const zone = entry.zone === -1 ? -1 : (entry.zone >= 0 && entry.zone <= 5 ? entry.zone : 0);
      
      // Each bar represents 30 seconds, so x position is in minutes
      const minutes = (index * 30 + 15) / 60; // Center of each 30-second bin
      
      // Get avg_heartrate for label
      const avgHeartrate = entry.avg_heartrate || null;

      chartData.push({
        x: minutes,
        y: zone,
        avgHeartrate: avgHeartrate
      });
    });

    return chartData;
  }, [selectedExercise]);

  // Process zone_summary data for chart
  const zoneSummaryChartData = useMemo(() => {
    if (!selectedExercise || !selectedExercise.zone_summary || typeof selectedExercise.zone_summary !== 'object') {
      return [];
    }

    // zone_summary format: {"0": 5, "1": 14, "2": 0.5, "-1": 10, ...}
    // Ignore zone=-1, only process zones 0-5
    const zoneCounts = [0, 0, 0, 0, 0, 0]; // Zones 0-5
    
    Object.keys(selectedExercise.zone_summary).forEach(zoneKey => {
      const zone = parseInt(zoneKey);
      const minutes = selectedExercise.zone_summary[zoneKey];
      // Ignore zone=-1 and only process valid zones 0-5
      if (zone >= 0 && zone <= 5 && typeof minutes === 'number') {
        zoneCounts[zone] = minutes;
      }
    });

    // Convert to chart data format
    return zoneCounts.map((minutes, zone) => ({
      x: zone,
      y: minutes
    }));
  }, [selectedExercise]);

  const activityMetrics = [
    {
      id: 'steps',
      name: 'Steps',
      bengaliName: 'পদক্ষেপ',
      icon: <StepsIcon />,
      description: 'Track your daily step count and walking activity',
      color: 'bg-blue-500',
      unit: 'steps',
      currentValue: '8,432'
    },
    {
      id: 'calories',
      name: 'Calories',
      bengaliName: 'ক্যালোরি',
      icon: <CaloriesIcon />,
      description: 'Monitor your calorie burn throughout the day',
      color: 'bg-green-500',
      unit: 'cal',
      currentValue: '2,156'
    },
    {
      id: 'distance',
      name: 'Distance',
      bengaliName: 'দূরত্ব',
      icon: <DistanceIcon />,
      description: 'Track the distance you cover during activities',
      color: 'bg-purple-500',
      unit: 'km',
      currentValue: '6.8'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-14">
      <div className="container mx-auto px-4 py-6">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium">ফিরে যান</span>
          </button>
        </div>
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">শারীরিক কার্যকলাপ</h1>
          <p className="text-gray-600">আপনার দৈনন্দিন শারীরিক কার্যকলাপ ট্র্যাক করুন</p>
        </div>

        {/* Platform Warning for Web */}
        {!Capacitor.isNativePlatform() && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-yellow-800 font-medium">Web Platform</span>
            </div>
            <p className="text-yellow-700 text-sm mt-1">
              Device exercise tracking features are not available on web platform. Use the mobile app for full functionality.
            </p>
          </div>
        )}

        {/* Exercise Card */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg shadow-lg overflow-hidden">
            {/* Exercise Header */}
            <div className="p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <ExerciseIcon />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">Exercise</h3>
                    <p className="text-sm opacity-90">ব্যায়াম</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{formatTime(elapsedTime)}</div>
                  <div className="text-xs opacity-90">সময়</div>
                </div>
              </div>
            </div>

            {/* Exercise Content */}
            <div className="p-6 bg-white">
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Start your workout session and track real-time metrics
              </p>
              
              {/* Real-time Metrics */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-blue-600">{exerciseData.stepCount}</div>
                  <div className="text-xs text-gray-500">Steps</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-600">{exerciseData.calories}</div>
                  <div className="text-xs text-gray-500">Calories</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-lg font-bold text-purple-600">{exerciseData.distance} km</div>
                  <div className="text-xs text-gray-500">Distance</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-lg font-bold text-red-600">{exerciseData.heartRate}</div>
                  <div className="text-xs text-gray-500">BPM</div>
                </div>
              </div>

              {/* Status Display */}
              {status && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-blue-700 text-sm font-medium">{status}</p>
                </div>
              )}

              {/* Start/Stop Button */}
              <button
                onClick={isExerciseActive ? stopExercise : startExercise}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors duration-200 ${
                  isExerciseActive
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isExerciseActive ? 'Stop Exercise' : 'Start Exercise'}
              </button>
            </div>
          </div>
        </div>

        {/* Exercise Record Section */}
        <div className="mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-6">Exercise Record</h3>
            
            {/* Error State */}
            {exerciseError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800">{exerciseError}</span>
                </div>
              </div>
            )}

            {/* Exercise List Dropdown */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Exercise Sessions</h4>
              {isLoadingList ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading exercise sessions...</span>
                </div>
              ) : exerciseList.length > 0 ? (
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-4 py-3 text-left bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors flex items-center justify-between"
                  >
                    <span className="text-sm text-gray-700">
                      {selectedExercise 
                        ? formatDateTime(selectedExercise.timestamp)
                        : 'Select an exercise session'
                      }
                    </span>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${isDropdownOpen ? 'transform rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  
                  {isDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsDropdownOpen(false)}
                      ></div>
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {exerciseList.map((exercise, index) => (
                          <button
                            key={index}
                            onClick={() => handleExerciseSelect(exercise)}
                            className={`w-full px-4 py-3 text-left text-sm hover:bg-blue-50 transition-colors ${
                              selectedExercise?.originalTimestamp === exercise.originalTimestamp
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-gray-700'
                            }`}
                          >
                            {formatDateTime(exercise.timestamp)}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : patientId ? (
                <p className="text-gray-600">No exercise data available</p>
              ) : (
                <p className="text-gray-600">No patient data available</p>
              )}
            </div>

            {/* Exercise Details Card */}
            {selectedExercise && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-800">Exercise Details</h4>
                  {isLoadingDetails && (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-600">Loading...</span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">Timestamp</div>
                    <div className="text-sm font-medium text-gray-800">
                      {formatDateTime(selectedExercise.timestamp)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">Heart Rate Min</div>
                    <div className="text-sm font-medium text-gray-800">
                      {selectedExercise.heartrate_min} bpm
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">Heart Rate Max</div>
                    <div className="text-sm font-medium text-gray-800">
                      {selectedExercise.heartrate_max} bpm
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">Heart Rate Avg</div>
                    <div className="text-sm font-medium text-gray-800">
                      {selectedExercise.heartrate_avg} bpm
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">Total Time</div>
                    <div className="text-sm font-medium text-gray-800">
                      {formatDuration(selectedExercise.total_time)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">Total Steps</div>
                    <div className="text-sm font-medium text-gray-800">
                      {selectedExercise.total_steps?.toLocaleString() || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">Total Distance</div>
                    <div className="text-sm font-medium text-gray-800">
                      {formatDistance(selectedExercise.total_distance)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">Total Calories</div>
                    <div className="text-sm font-medium text-gray-800">
                      {selectedExercise.total_calories || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">Avg Speed</div>
                    <div className="text-sm font-medium text-gray-800">
                      {formatSpeed(selectedExercise.avg_speed)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">VO2 Max</div>
                    <div className="text-sm font-medium text-gray-800">
                      {selectedExercise.vo2max} ml/kg/min
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-bold">Cardio Recovery Score</div>
                    <div className="text-sm font-medium text-gray-800">
                      {selectedExercise.cardio_recovery_score} bpm
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Heart Rate Zone Map Chart */}
            {selectedExercise && zoneMapChartData && zoneMapChartData.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Heart Rate Zone Map</h4>
                <div className="w-full overflow-x-auto">
                  <VictoryChart
                    domainPadding={{ x: [10, 10] }}
                    padding={{ top: 40, bottom: 50, left: 60, right: 20 }}
                    width={Math.max(600, Math.max(zoneMapChartData.length * 40, 400))}
                    height={250}
                    domain={{
                      x: zoneMapChartData.length > 0 
                        ? [0, (zoneMapChartData.length * 30) / 60]
                        : [0, 1],
                      y: [-1, 5]
                    }}
                  >
                    <VictoryAxis
                      label="Time (minutes)"
                      style={{
                        axisLabel: { padding: 35, fontSize: 12, fill: '#374151' },
                        tickLabels: { fontSize: 10, fill: '#6B7280', angle: -45, textAnchor: 'end' }
                      }}
                      tickFormat={(t) => {
                        if (t % 1 === 0) {
                          return `${t}m`;
                        }
                        const mins = Math.floor(t);
                        const secs = Math.round((t - mins) * 60);
                        if (mins === 0) {
                          return `${secs}s`;
                        }
                        return `${mins}m ${secs}s`;
                      }}
                    />
                    <VictoryAxis
                      dependentAxis
                      label="Zone"
                      tickValues={[-1, 0, 1, 2, 3, 4, 5]}
                      tickFormat={(t) => t === -1 ? 'No Data' : `Zone ${t}`}
                      domain={[-1, 5]}
                      style={{
                        axisLabel: { padding: 45, fontSize: 12, fill: '#374151' },
                        tickLabels: { fontSize: 10, fill: '#6B7280' }
                      }}
                    />
                    <VictoryBar
                      data={zoneMapChartData}
                      labels={({ datum }) => datum.avgHeartrate ? `${Math.round(datum.avgHeartrate)}` : ''}
                      labelComponent={
                        <VictoryLabel
                          dy={-8}
                          style={{ fontSize: 9, fill: '#374151', fontWeight: 'bold' }}
                        />
                      }
                      style={{
                        data: { 
                          fill: ({ datum }) => {
                            const colors = [
                              '#9CA3AF', // Zone -1 (No Data) - Dark Gray
                              '#E5E7EB', // Zone 0 - Light Gray
                              '#3B82F6', // Zone 1 - Blue
                              '#10B981', // Zone 2 - Green
                              '#F59E0B', // Zone 3 - Orange
                              '#EF4444', // Zone 4 - Red
                              '#8B5CF6'  // Zone 5 - Purple
                            ];
                            const colorIndex = datum.y === -1 ? 0 : datum.y + 1;
                            return colors[colorIndex] || '#E5E7EB';
                          },
                          stroke: '#374151',
                          strokeWidth: 0.5
                        },
                        labels: { fontSize: 9, fill: '#374151', fontWeight: 'bold' }
                      }}
                      barWidth={30}
                    />
                  </VictoryChart>
                </div>
              </div>
            )}

            {/* Zone Summary Chart */}
            {selectedExercise && zoneSummaryChartData && zoneSummaryChartData.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Zone Summary</h4>
                <div className="w-full">
                  <VictoryChart
                    domainPadding={{ x: 20 }}
                    padding={{ top: 60, bottom: 60, left: 60, right: 20 }}
                    width={600}
                    height={300}
                    domain={{ x: [-0.2, 5.5] }}
                  >
                    <VictoryAxis
                      label="Zone"
                      tickValues={[0, 1, 2, 3, 4, 5]}
                      tickFormat={(t) => `Zone ${t}`}
                      style={{
                        axisLabel: { padding: 35, fontSize: 12, fill: '#374151' },
                        tickLabels: { fontSize: 10, fill: '#6B7280' }
                      }}
                    />
                    <VictoryBar
                      data={zoneSummaryChartData}
                      labels={({ datum }) => datum.y > 0 ? `${datum.y} min` : ''}
                      labelComponent={
                        <VictoryLabel
                          dy={-5}
                          style={{ fontSize: 11, fill: '#374151', fontWeight: 'bold' }}
                        />
                      }
                      style={{
                        data: { 
                          fill: ({ datum }) => {
                            const colors = [
                              '#E5E7EB', // Zone 0 - Gray
                              '#3B82F6', // Zone 1 - Blue
                              '#10B981', // Zone 2 - Green
                              '#F59E0B', // Zone 3 - Orange
                              '#EF4444', // Zone 4 - Red
                              '#8B5CF6'  // Zone 5 - Purple
                            ];
                            return colors[datum.x] || '#E5E7EB';
                          },
                          stroke: '#374151',
                          strokeWidth: 1
                        },
                        labels: { fontSize: 11, fill: '#374151', fontWeight: 'bold' }
                      }}
                      barWidth={50}
                    />
                  </VictoryChart>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Activity Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activityMetrics.map((metric) => (
            <div
              key={metric.id}
              onClick={() => handleCardClick(metric.id)}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer overflow-hidden group"
            >
              {/* Metric Header */}
              <div className={`${metric.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      {metric.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{metric.name}</h3>
                      <p className="text-sm opacity-90">{metric.bengaliName}</p>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>

              {/* Metric Content */}
              <div className="p-6">
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {metric.description}
                </p>
                
                {/* Current Value */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-gray-800">{metric.currentValue}</span>
                    <span className="text-gray-500 ml-1">{metric.unit}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">বর্তমান মান</div>
                    <div className="text-xs text-green-600">+5.2%</div>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">গড়</span>
                      <div className="font-medium text-gray-700">{metric.currentValue} {metric.unit}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">লক্ষ্য</span>
                      <div className="font-medium text-gray-700">
                        {metric.id === 'steps' ? '10,000' : 
                         metric.id === 'calories' ? '2,500' : '8.0'} {metric.unit}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 30-Minute Reminder Dialog */}
        {showReminder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  30 Minutes Completed
                </h3>
                <p className="text-gray-600 text-sm mb-6">
                  You've been exercising for 30 minutes. Would you like to continue tracking your exercise?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleContinueExercise}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Continue
                  </button>
                  <button
                    onClick={handleStopExercise}
                    className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Stop
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activity; 